import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../../firebase/AuthProvider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import FileViewer from "react-file-viewer";
import SpreadsheetEditor from "./excel/SpreadsheetEditor"; // Reusing your existing Excel component
import { formatFileSize } from "../../../utilities/fileUtils";
import { formatDateTime } from "../../../utilities/dateUtils";
import backarrowbtn from '../../../assets/images/backarrowbtn.svg';
import { EmployeeFile } from "../../../interfaces/employeeFiles/EmployeeFiles";
import { toast } from "react-toastify";
import { downloadEmployeeFileBlob, getEmployeeFileMetadata, getEmployeeFileViewUrl } from "../../../services/EmployeeFileService/EmployeeFileService";
import EmployeeFilePDFViewer from "./pdfViewer/EmployeeFilePDFViewer";

const EmployeeFileViewPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { fileId } = useParams<{ fileId: string }>();
  const navigate = useNavigate();

  const [fileUrl, setFileUrl] = useState<string>("");
  const [fileMetadata, setFileMetadata] = useState<EmployeeFile | null>(null);
  const [blobMimeType, setBlobMimeType] = useState<string>("");
  const [textContent, setTextContent] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchFile = async () => {
      if (!currentUser || !fileId) return;

      try {
        setLoading(true);
        const authToken = await currentUser.getIdToken();

        // 1. Fetch Metadata
        const metadata = await getEmployeeFileMetadata(authToken, fileId);
        if (!metadata) {
          toast.error("Could not load file details.");
          setLoading(false);
          return;
        }
        setFileMetadata(metadata);

        // 2. Fetch Blob URL for Viewing
        const url = await getEmployeeFileViewUrl(authToken, fileId);
        if (!url) {
            toast.error("Could not load file content.");
            setLoading(false);
            return;
        }
        setFileUrl(url);

        // 3. Determine MIME type for renderer
        // Fetch the blob again just to inspect type if metadata is missing strict mime
        // (Optimized: we could modify getEmployeeFileViewUrl to return the blob too)
        const blobRes = await downloadEmployeeFileBlob(authToken, fileId);
        if(blobRes) {
            const inferredMimeType = metadata.mimeType || blobRes.type;
            setBlobMimeType(inferredMimeType);

            // If text, read content for display
            if (inferredMimeType === "text/plain") {
                const text = await blobRes.text();
                setTextContent(text);
            }
        }
      } catch (error: any) {
        console.error("Error fetching file:", error);
        toast.error("Error loading file.");
      } finally {
        setLoading(false);
      }
    };

    fetchFile();
    
    // Cleanup Object URL on unmount
    return () => {
        if (fileUrl) URL.revokeObjectURL(fileUrl);
    };
  }, [currentUser, fileId]);

  const handleDownload = async () => {
    if (!currentUser || !fileMetadata || !fileId) return;

    try {
      const authToken = await currentUser.getIdToken();
      const blob = await downloadEmployeeFileBlob(authToken, fileId);

      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileMetadata.fileName; // Note: 'fileName' vs 'name' in UserFile
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Download failed.");
    }
  };

  const renderViewer = () => {
    if (loading || !fileUrl) return <div className="p-5">Loading file content...</div>;

    const extension = fileMetadata?.fileName?.split(".").pop()?.toLowerCase();

    // PDF Handler
    if (blobMimeType === "application/pdf" || extension === "pdf") {
      return <EmployeeFilePDFViewer fileUrl={fileUrl} scale={1.0} />;
    }

    // Image Handler
    if (blobMimeType.startsWith("image/")) {
      return (
        <div className="flex flex-col items-center overflow-hidden h-full justify-center bg-gray-100">
          <img src={fileUrl} alt={fileMetadata?.fileName} className="max-w-full max-h-full object-contain" />
        </div>
      );
    }

    // Text Handler
    if (blobMimeType === "text/plain") {
      return (
        <pre className="p-4 bg-white overflow-auto h-full">{textContent}</pre>
      );
    }

    // Video Handler
    if (blobMimeType.startsWith("video/")) {
      return <video src={fileUrl} controls className="w-full h-full" />;
    }

    // Spreadsheet Handler
    if (blobMimeType.includes("spreadsheet") || ["xlsx", "xls", "csv"].includes(extension || "")) {
      return (
        <div className="overflow-auto h-full">
          <SpreadsheetEditor fileUrl={fileUrl} />
        </div>
      );
    }

    // Generic Handler
    return <FileViewer fileType={extension || "unknown"} filePath={fileUrl} onError={(e: any) => console.log('Viewer error', e)} />;
  };

  return (
    <div className="common_card h-full flex flex-col">
      {/* Header */}
      <div className="top top_bar_flx shrink-0 mb-3">
        <div className="top_bar_tittle flex justify-start align-middle items-center">
            <button onClick={() => navigate(-1)} className="mr-2"> 
                <img style={{ width: '1.5rem' }} src={backarrowbtn} alt="Back" /> 
            </button>
          <h2 className="m-0" title={fileMetadata?.fileName}>
            {fileMetadata?.fileName || "Loading..."}
          </h2>
        </div>
        <div className="top_right_flx">
          <button className="border px-3 py-1 rounded hover:bg-gray-100" onClick={handleDownload} title="Download">
            <FontAwesomeIcon icon={faDownload} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="sign_body flex-1 overflow-hidden">
        {/* Left: Viewer */}
        <div className="sign_left h-full overflow-hidden relative">
          {renderViewer()}
        </div>

        {/* Right: Metadata */}
        <div className="sign_right overflow-auto">
          <div className="common_card h-full">
            <div className="top top_bar_flx border_tittle">
              <div className="top_bar_tittle">
                <h2>Document Info</h2>
              </div>
            </div>
            <div className="document_stats_grid mb-3 p-3">
              <div className="mb-3">
                <div className="font-bold text-gray-600">File Type</div>
                <div className="text-wrap break-words">
                  {fileMetadata?.mimeType || "N/A"}
                </div>
              </div>
              <div className="mb-3">
                <div className="font-bold text-gray-600">Created</div>
                <div>
                  {fileMetadata?.dateAdded
                    ? formatDateTime(new Date(fileMetadata.dateAdded))
                    : "N/A"}
                </div>
              </div>
              <div className="mb-3">
                <div className="font-bold text-gray-600">File Size</div>
                <div>{formatFileSize(fileMetadata?.size || 0)}</div>
              </div>
              <div>
                <div className="font-bold text-gray-600">Category</div>
                <div>{fileMetadata?.documentCategory?.documentCategoryName || "General"}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeFileViewPage;